const CLIENT_ID = import.meta.env.VITE_GOOGLE_DRIVE_CLIENT_ID;
const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file';
const CATALOG_FOLDER_NAME = 'Catálogo BNI';
const CATALOG_FOLDER_CACHE_KEY = 'bni_catalog_folder_id';

interface TokenClient {
  requestAccessToken: (options?: { prompt?: string }) => void;
}

interface TokenResponse {
  access_token?: string;
  error?: string;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: TokenResponse) => void;
          }) => TokenClient;
        };
      };
    };
  }
}

let scriptLoadPromise: Promise<void> | null = null;
let cachedToken: { value: string; expiresAt: number } | null = null;

export function preloadGoogleIdentity(): void {
  loadGoogleIdentityScript().catch(() => {
    // Falha silenciosa aqui é aceitável: getAccessToken tentará carregar de novo e reportará o erro ao usuário.
  });
}

function loadGoogleIdentityScript(): Promise<void> {
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Não foi possível carregar o Google Identity Services.'));
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
}

export function isGoogleDriveAuthorized(): boolean {
  return !!cachedToken && cachedToken.expiresAt > Date.now() + 30_000;
}

export async function getAccessToken(): Promise<string> {
  if (cachedToken && cachedToken.expiresAt > Date.now() + 30_000) {
    return cachedToken.value;
  }

  await loadGoogleIdentityScript();

  return new Promise((resolve, reject) => {
    let settled = false;

    const timeoutId = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(
        new Error(
          'A janela de autorização do Google não respondeu. Verifique se o navegador não bloqueou o pop-up (ex: Brave Shields ou bloqueador de pop-up) e tente novamente.'
        )
      );
    }, 25_000);

    const client = window.google!.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: DRIVE_SCOPE,
      callback: (response) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        if (response.error || !response.access_token) {
          reject(new Error('Autorização do Google Drive foi cancelada ou falhou.'));
          return;
        }
        cachedToken = { value: response.access_token, expiresAt: Date.now() + 55 * 60 * 1000 };
        resolve(response.access_token);
      },
    });

    try {
      client.requestAccessToken({ prompt: cachedToken ? '' : 'consent' });
    } catch (err) {
      if (!settled) {
        settled = true;
        window.clearTimeout(timeoutId);
        reject(err instanceof Error ? err : new Error('Falha ao solicitar autorização do Google Drive.'));
      }
    }
  });
}

async function driveFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await getAccessToken();
  const response = await fetch(`https://www.googleapis.com/drive/v3/${path}`, {
    ...init,
    headers: {
      ...init.headers,
      Authorization: `Bearer ${token}`,
    },
  });
  if (!response.ok) {
    const body = await response.text();
    throw new Error(`Erro na API do Google Drive (${response.status}): ${body}`);
  }
  return response;
}

async function findFolder(name: string, parentId?: string): Promise<string | null> {
  const parentClause = parentId ? ` and '${parentId}' in parents` : " and 'root' in parents";
  const q = encodeURIComponent(
    `mimeType='application/vnd.google-apps.folder' and name='${name.replace(/'/g, "\\'")}' and trashed=false${parentClause}`
  );
  const response = await driveFetch(`files?q=${q}&fields=files(id,name)&spaces=drive`);
  const data = await response.json();
  return data.files?.[0]?.id ?? null;
}

async function createFolder(name: string, parentId?: string): Promise<string> {
  const response = await driveFetch('files?fields=id', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name,
      mimeType: 'application/vnd.google-apps.folder',
      parents: parentId ? [parentId] : undefined,
    }),
  });
  const data = await response.json();
  return data.id as string;
}

async function findOrCreateFolder(name: string, parentId?: string): Promise<string> {
  const existing = await findFolder(name, parentId);
  if (existing) return existing;
  return createFolder(name, parentId);
}

export async function ensureCatalogFolder(): Promise<string> {
  const cached = localStorage.getItem(CATALOG_FOLDER_CACHE_KEY);
  if (cached) {
    try {
      await driveFetch(`files/${cached}?fields=id,trashed`);
      return cached;
    } catch {
      localStorage.removeItem(CATALOG_FOLDER_CACHE_KEY);
    }
  }

  const folderId = await findOrCreateFolder(CATALOG_FOLDER_NAME);
  localStorage.setItem(CATALOG_FOLDER_CACHE_KEY, folderId);
  return folderId;
}

export async function ensurePropertyFolder(propertyTitle: string): Promise<string> {
  const catalogFolderId = await ensureCatalogFolder();
  const safeTitle = propertyTitle.trim() || 'Imóvel sem título';
  return findOrCreateFolder(safeTitle, catalogFolderId);
}

async function makePubliclyReadable(fileId: string): Promise<void> {
  await driveFetch(`files/${fileId}/permissions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ role: 'reader', type: 'anyone' }),
  });
}

export interface UploadedPhoto {
  id: string;
  url: string;
}

export async function uploadPhotoToDrive(file: File, folderId: string): Promise<UploadedPhoto> {
  const token = await getAccessToken();

  const metadata = { name: file.name, parents: [folderId] };
  const form = new FormData();
  form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
  form.append('file', file);

  const response = await fetch(
    'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id',
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    }
  );

  if (!response.ok) {
    const body = await response.text();
    throw new Error(`Falha ao enviar foto para o Google Drive (${response.status}): ${body}`);
  }

  const data = await response.json();
  const fileId = data.id as string;

  await makePubliclyReadable(fileId);

  return {
    id: fileId,
    url: `https://lh3.googleusercontent.com/d/${fileId}=w1600`,
  };
}

export async function deletePhotoFromDrive(fileId: string): Promise<void> {
  await driveFetch(`files/${fileId}`, { method: 'DELETE' });
}
